import React, { useEffect, useState } from "react";
import { STATUS_META, INTENT_META, intentMeta, initials, hueFromString, recordTypeMeta, curateLabel } from "../utils";

export function StatusPill({ status, small }) {
  const meta = STATUS_META[status] || { label: status || "—", tone: "neutral" };
  return (
    <span className={`ob-pill ob-pill--${meta.tone} ${meta.pulse ? "is-pulsing" : ""} ${small ? "ob-pill--sm" : ""}`}>
      <span className="ob-pill__dot" />
      {meta.label}
    </span>
  );
}

// Pass `doc` to get close-out specific text ("Close - Lost"); `intent` alone still works.
export function IntentPill({ intent, doc }) {
  const value = intent || (doc && doc.ai && doc.ai.intent);
  if (!value) return <span className="ob-muted">—</span>;
  const meta = doc ? intentMeta(doc) : INTENT_META[value] || { label: value, tone: "neutral" };
  return <span className={`ob-pill ob-pill--${meta.tone} ob-pill--sm`}>{meta.label}</span>;
}

// "Direct request" tag for emails sent straight to the bot (no forwarded thread)
export function ModePill({ mode }) {
  if (mode !== "request") return null;
  return (
    <span className="ob-pill ob-pill--neutral ob-pill--sm" title="Sent directly to the bot - no forwarded thread">
      Direct request
    </span>
  );
}

// Business line pill: "Sponsorship" / "Curate · Curate 2"
export function RecordTypePill({ name, curateCategory, size = "sm" }) {
  if (!name) return null;
  const meta = recordTypeMeta(name);
  return (
    <span className={`ob-pill ob-pill--${meta.tone} ob-pill--${size}`} title={meta.blurb || name}>
      {meta.short}
      {curateCategory ? ` · ${curateLabel(curateCategory)}` : ""}
    </span>
  );
}

export function ConfidenceBar({ value, threshold }) {
  if (value === null || value === undefined) return <span className="ob-muted">—</span>;
  const pct = Math.round(Number(value) * 100);
  const tone = threshold !== undefined ? (value >= threshold ? "success" : value >= threshold - 0.2 ? "warning" : "danger") : "info";
  return (
    <span className={`ob-confidence ob-confidence--${tone}`} title={`AI confidence ${pct}%${threshold !== undefined ? ` · auto-apply threshold ${Math.round(threshold * 100)}%` : ""}`}>
      <span className="ob-confidence__track">
        <span className="ob-confidence__fill" style={{ width: `${pct}%` }} />
        {threshold !== undefined && <span className="ob-confidence__threshold" style={{ left: `${Math.round(threshold * 100)}%` }} />}
      </span>
      <span className="ob-confidence__value">{pct}%</span>
    </span>
  );
}

export function Avatar({ name, email, size = 30 }) {
  const hue = hueFromString(email || name);
  return (
    <span
      className="ob-avatar"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: `hsl(${hue} 55% 92%)`, color: `hsl(${hue} 45% 32%)` }}
      title={email || name}
    >
      {initials(name, email)}
    </span>
  );
}

export function Panel({ title, eyebrow, actions, children, className = "", tone }) {
  return (
    <section className={`ob-panel ${tone ? `ob-panel--${tone}` : ""} ${className}`}>
      {(title || actions) && (
        <header className="ob-panel__head">
          <div>
            {eyebrow && <div className="ob-eyebrow">{eyebrow}</div>}
            {title && <h2 className="ob-panel__title">{title}</h2>}
          </div>
          {actions && <div className="ob-panel__actions">{actions}</div>}
        </header>
      )}
      <div className="ob-panel__body">{children}</div>
    </section>
  );
}

export function KeyValueList({ items }) {
  const rows = (items || []).filter((i) => i && i.value !== undefined && i.value !== null && i.value !== "");
  if (!rows.length) return <div className="ob-muted">Nothing extracted</div>;
  return (
    <dl className="ob-kv">
      {rows.map((i) => (
        <div className="ob-kv__row" key={i.key}>
          <dt>{i.key}</dt>
          <dd>{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EmptyState({ title, children, icon }) {
  return (
    <div className="ob-empty">
      {icon && <div className="ob-empty__icon">{icon}</div>}
      <h3>{title}</h3>
      <div className="ob-empty__body">{children}</div>
    </div>
  );
}

export function CopyButton({ text, label = "Copy", className = "" }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <button
      type="button"
      className={`ob-btn ob-btn--ghost ob-btn--sm ${className}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
        } catch (_e) {
          /* ignore */
        }
      }}
    >
      {copied ? "Copied" : label}
    </button>
  );
}

export function Spinner({ label }) {
  return (
    <div className="ob-spinner">
      <span className="ob-spinner__ring" />
      {label && <span>{label}</span>}
    </div>
  );
}

export function ErrorBox({ error, onRetry }) {
  return (
    <div className="ob-error">
      <span>{String(error || "Something went wrong")}</span>
      {onRetry && (
        <button type="button" className="ob-btn ob-btn--sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function ExternalLink({ href, children, className = "" }) {
  if (!href) return <span className="ob-muted">—</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={`ob-link ${className}`}>
      {children}
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    </a>
  );
}

// Salesforce-ish record icon
export function SfIcon({ kind }) {
  const map = {
    Account: { bg: "#7f8de1", txt: "A" },
    Contact: { bg: "#a094ed", txt: "C" },
    Opportunity: { bg: "#fcb95b", txt: "O" },
    OpportunityContactRole: { bg: "#8fd0e1", txt: "R" },
    Task: { bg: "#4bc076", txt: "T" },
  };
  const m = map[kind] || { bg: "#cbd5e1", txt: "?" };
  return (
    <span className="ob-sficon" style={{ background: m.bg }} aria-hidden="true">
      {m.txt}
    </span>
  );
}
